import { motion } from "framer-motion";
import { useRef, useEffect, useState } from "react";

const easing = [0.16, 1, 0.3, 1];

/**
 * StaggerWords — splits children string into words and reveals each with stagger.
 * Honors highlighted color spans via a [[mulberry]]highlight[[/]] inline token.
 */
export function StaggerWords({
  text,
  className = "",
  baseDelay = 0,
  stagger = 0.05,
  highlight,
  highlightColor = "var(--mulberry)",
  duration = 0.9,
  yOffset = 28,
  as = "span",
}) {
  const words = text.split(/(\s+)/);
  const Tag = motion[as] || motion.span;
  return (
    <Tag
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
      transition={{ staggerChildren: stagger, delayChildren: baseDelay }}
    >
      {words.map((w, i) => {
        if (/^\s+$/.test(w))
          return (
            <span key={i} aria-hidden style={{ whiteSpace: "pre" }}>
              {w}
            </span>
          );
        const isHL = highlight && highlight.includes(w.replace(/[.,]/g, ""));
        return (
          <span
            key={i}
            style={{ display: "inline-block", overflow: "hidden", verticalAlign: "bottom" }}
          >
            <motion.span
              style={{
                display: "inline-block",
                color: isHL ? highlightColor : undefined,
              }}
              variants={{
                hidden: { y: yOffset, opacity: 0 },
                visible: {
                  y: 0,
                  opacity: 1,
                  transition: { duration, ease: easing },
                },
              }}
            >
              {w}
            </motion.span>
          </span>
        );
      })}
    </Tag>
  );
}

/**
 * StaggerList — renders children with cascading reveal as they enter viewport.
 */
export function StaggerList({
  children,
  stagger = 0.08,
  delayChildren = 0,
  className = "",
}) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
      transition={{ staggerChildren: stagger, delayChildren }}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, y = 22, duration = 0.7, className = "" }) {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y },
        visible: { opacity: 1, y: 0, transition: { duration, ease: easing } },
      }}
    >
      {children}
    </motion.div>
  );
}

/**
 * useInViewActive — tracks which of a list of refs is currently in view (top-most).
 */
export function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const observers = [];
    const visible = new Map();
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          visible.set(id, entry.intersectionRatio);
          // pick the id with the highest visibility, fallback to first in view
          let best = null;
          let bestRatio = 0;
          visible.forEach((ratio, key) => {
            if (ratio > bestRatio) {
              bestRatio = ratio;
              best = key;
            }
          });
          if (best) setActive(best);
        },
        { threshold: [0, 0.25, 0.5, 0.75, 1], rootMargin: "-30% 0px -50% 0px" }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [ids]);
  return active;
}

/**
 * CountUp — animates a number from 0 to `to` when in view.
 */
export function CountUp({ to, duration = 1.4, className = "", format = (v) => v }) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const start = performance.now();
          const tick = (now) => {
            const t = Math.min((now - start) / (duration * 1000), 1);
            const eased = 1 - Math.pow(1 - t, 3);
            setValue(Math.round(eased * to));
            if (t < 1) raf = requestAnimationFrame(tick);
          };
          raf = requestAnimationFrame(tick);
          obs.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      obs.disconnect();
    };
  }, [to, duration]);
  return (
    <span ref={ref} className={className}>
      {format(value)}
    </span>
  );
}

/**
 * Parallax — translates element on scroll relative to viewport position.
 */
export function Parallax({ children, speed = 0.18, className = "" }) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const center = rect.top + rect.height / 2 - vh / 2;
      setOffset(-center * speed);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [speed]);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        transform: `translate3d(0, ${offset}px, 0)`,
        willChange: "transform",
      }}
    >
      {children}
    </div>
  );
}

/**
 * DrawLine — animates a horizontal 1px line drawing from left to right on enter.
 */
export function DrawLine({ delay = 0, color = "var(--mulberry)", className = "" }) {
  return (
    <motion.span
      className={className}
      style={{
        display: "block",
        height: 1,
        background: color,
        transformOrigin: "left",
      }}
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 1.1, delay, ease: easing }}
    />
  );
}
